import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTheme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { useSetState } from "Helpers/Hooks";
import MuiButton from "Components/MuiButton/MuiButton";
import DialogContent from "@material-ui/core/DialogContent";
import Translator from "Languages/Utility/Translator";
import { DialogActions } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import BackDropLoading from "Components/BackDropLoading/BackDropLoading";
import * as R from "ramda";
import { read } from "Helpers/LocalStorage";
import showNotify from "Helpers/Notify";
import {
  editSettingUnitAttachment,
  getSettingUnitAttachments,
  insertSettingUnitAttachment,
} from "Stores/actions/properties/ShopAction";
import ActionFormatter from "Components/Table/ActionFormatter/ActionFormatter";
import * as Icons from "react-bootstrap-icons";
import { toggleMedia } from "Constants/Constants";
import { TabContext, TabList, TabPanel } from "@material-ui/lab";
import { Box, Tab } from "@material-ui/core";
import AddNewLink from "./AddNewLink";
import AddedLinks from "./AddedLinks";

export default function ProjectUnitDetails({ row }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formValid, setFormValid] = useState({});
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [checkValid, setCheckValid] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(-1);
  const [tab, setTab] = useState("addNewLink");

  const [data, setData] = useState([]);

  const { selectedProject } = useSelector((state) => ({
    selectedProject: state.shop.selectedProject,
  }));

  const [controls, setControls] = useSetState({
    selectType: {
      type: "toggle",
      arrayItems: toggleMedia,
      value: toggleMedia[0],
      validation: {
        required: true,
      },
    },
    link: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
    },
    shortDescribe: {
      type: "text",
      value: "",
      validation: {
        required: false,
      },
    },
  });

  const handleDialog = () => setOpenDialog(!openDialog);

  const handleChange = (event, newTab) => {
    setTab(newTab);
    if (editMode) {
      setEditMode(false);
      onClear();
    }
  };

  const onEnter = async () => {
    await get_addedAttachment();
    onClear();
  };

  const onClear = async () => {
    setControls({
      selectType: {
        value: toggleMedia[0],
      },
    });
    setControls({ link: { value: "" } });
    setControls({ shortDescribe: { value: "" } });
    setFormValid({ link: false, shortDescribe: false });
    setCheckValid(false);
    setSelectedUnit(-1);
  };

  const setReload = async () => {
    await get_addedAttachment();
  };

  const editUnit = (id) => {
    const selected = data.find((i) => i.Id === id);
    if (selected) {
      setEditMode(true);
      setControls({
        selectType: {
          value: toggleMedia.find((i) => i.value === selected.TypeLink),
        },
      });
      setControls({ link: { value: selected.Link } });
      setControls({ shortDescribe: { value: selected.Description } });
      setFormValid({ link: true, shortDescribe: true });
      setCheckValid(true);
      handleChange(null, "addNewLink");
      setSelectedUnit(id);
    }
  };

  const get_addedAttachment = async () => {
    setLoading(true);
    const data = await dispatch(
      getSettingUnitAttachments({
        KeyGroup: read("groupKey"),
        KeyUser: read("userKey"),
        idUnit: row.Id,
        idProject: selectedProject.IdProject,
      })
    );
    setData(data);
    setLoading(false);
  };

  const setValue = async (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const onChangeToggle = async (name, key, value, option = null) => {
    setControls({ [name]: { value: value } });
  };

  const submitUnit = async (e, isEdit = false) => {
    e.preventDefault();
    if (
      !R.isEmpty(formValid) &&
      Object.values(formValid).every((item) => item === true) &&
      (!isEdit || selectedUnit !== -1)
    ) {
      const { selectType, link, shortDescribe } = controls;
      try {
        const data = {
          KeyGroup: read("groupKey"),
          KeyUser: read("userKey"),
          idUnit: row.Id,
          typeLink: selectType.value.value,
          link: link.value,
          description: shortDescribe.value,
          ...(isEdit && { id: selectedUnit }),
        };
        setLoading(true);
        if (isEdit) {
          await dispatch(editSettingUnitAttachment(data));
        } else {
          await dispatch(insertSettingUnitAttachment(data));
        }
        onClear();
        handleChange(null, "addedLinks");
        await get_addedAttachment();
        setLoading(false);
      } catch (e) {
        showNotify({
          text: t("correctFormDataRequired"),
          customClass: "error",
        });
      }
    } else {
      setCheckValid(true);
    }
  };

  const onExit = () => {
    setOpenDialog(false);
  };

  return (
    <>
      <ActionFormatter
        title={<Translator id="unitDetailsAttachment" />}
        icon={<Icons.Paperclip size={17} />}
        className="primary-button"
        onClick={handleDialog}
      />
      <Dialog
        dir={direction}
        open={openDialog}
        TransitionProps={{ onEnter: onEnter, onExit: onExit }}
        onClose={handleDialog}
        fullWidth
        maxWidth="sm"
      >
        <div>
          <div className="dialogHeader">
            <p>{`${t("unitDetailsAttachment")} (${row.Name})`}</p>
          </div>
          <DialogContent id="content-dialog-manager">
            <BackDropLoading loading={loading} />

            <TabContext value={tab}>
              <Box>
                <TabList
                  onChange={handleChange}
                  variant="scrollable"
                  className={`dialog-tab${editMode ? " edit-mode" : ""}`}
                >
                  <Tab
                    icon={<Icons.FileEarmarkPlus />}
                    label={editMode ? t("editLink") : t("addNewLink")}
                    value="addNewLink"
                  />
                  <Tab
                    icon={<Icons.Folder />}
                    label={t("addedLinks")}
                    value="addedLinks"
                  />
                </TabList>
              </Box>
              <TabPanel
                value="addNewLink"
                className="setting-unit-details-dialog"
              >
                <AddNewLink
                  controls={controls}
                  onChangeToggle={onChangeToggle}
                  setValue={setValue}
                  checkValid={checkValid}
                  formValid={formValid}
                />
              </TabPanel>
              <TabPanel
                value="addedLinks"
                className="setting-unit-details-dialog"
              >
                <AddedLinks
                  data={data}
                  idUnit={row.Id}
                  setReload={setReload}
                  onEdit={editUnit}
                />
              </TabPanel>
            </TabContext>
          </DialogContent>
          <DialogActions>
            <MuiButton
              loading={loading}
              title={<Translator id={editMode ? "virayesh" : "button_save"} />}
              onClick={(e) => submitUnit(e, editMode)}
              className={editMode ? "MuiButton-warning" : ""}
              hidden={tab !== "addNewLink"}
              disabled={tab !== "addNewLink"}
            />
            <MuiButton
              onClick={handleDialog}
              title={<Translator id="enseraf" />}
            />
          </DialogActions>
        </div>
      </Dialog>
    </>
  );
}
